<script lang="ts" setup>
import { Button } from '@progress/kendo-vue-buttons';
import { FieldWrapper, FormElement } from '@progress/kendo-vue-form';
import { Switch } from '@progress/kendo-vue-inputs';
import { Popup } from '@progress/kendo-vue-popup';
import { ref } from 'vue';

import { container } from '@/container';
import { t } from '@/locales';
import { BetaFeatures } from '@/services/BetaFeatures';

// Services.
const betaFeatures = container.get(BetaFeatures);

// Refs.
const $wrapper = ref<InstanceType<typeof FormElement>>();
const $popup = ref<InstanceType<typeof Popup>>();
const show = ref(false);

// Methods.
const onFocusOut = (e: FocusEvent) => {
  const { relatedTarget } = e;
  // Ignore elements inside the popup.
  if (relatedTarget && ($wrapper.value?.$el.contains(relatedTarget) || $popup.value?.$el.contains(relatedTarget))) {
    return;
  }
  show.value = false;
};

defineExpose({ $wrapper, $popup });
</script>

<template>
  <FormElement
    ref="$wrapper"
    class="ml-3"
    tabindex="-1"
    :horizontal="true"
    @focusout.capture="onFocusOut"
  >
    <Button
      ref="betaFeaturesToggle"
      fill-mode="outline"
      theme-color="primary"
      :selected="show"
      @click.prevent.stop="show = !show"
    >
      {{ `${t('betaFeatures')} ${betaFeatures.enabledBetaFeatures.length}/${betaFeatures.length}` }}
      <Switch
        :value="betaFeatures.allBetaFeaturesEnabled"
        class="ml-2"
        size="small"
        off-label="ALL"
        on-label="ALL"
        @change="() => betaFeatures.toggleAll()"
        @click.stop=""
      />
    </Button>
    <Popup
      ref="$popup"
      tabindex="-1"
      :anchor="'betaFeaturesToggle'"
      :anchor-align="{ horizontal: 'right', vertical: 'bottom' }"
      :popup-align="{ horizontal: 'right', vertical: 'top' }"
      :collision="{
        horizontal: 'fit',
        vertical: 'flip',
      }"
      :offset="{ top: 20, left: 0 }"
      :show="show"
      :popup-class="'beta-features mt-1 px-3 py-2 gap-2'"
      @focusout.capture="onFocusOut"
    >
      <FieldWrapper
        v-for="betaFeature in betaFeatures"
        :key="betaFeature.id"
        class="flex align-items-center justify-content-between"
      >
        <div
          class="label"
          :title="betaFeature.label"
        >
          <a
            v-if="betaFeature.link"
            :href="betaFeature.link"
            target="_blank"
          >
            {{ betaFeature.label }}
          </a>
          <template v-else>
            {{ betaFeature.label }}
          </template>
        </div>
        <Switch
          v-model="betaFeature.enabled"
          class="ml-2"
          size="small"
        />
      </FieldWrapper>
    </Popup>
  </FormElement>
</template>

<style lang="scss">
.beta-features {
  font-family: var(--font-heading);
  width: 250px;

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre;
  }
}
</style>
